import React, { useCallback, useEffect, useMemo, useState } from "react";

import Drawer from "veneer/Drawer";
import { showSuccess } from "veneer/SnackBar";

import {
  alertPresetsList,
  customAlertPreset,
  groupedPresetOptions,
  sourceOptionsMap,
} from "cortex/ui/src/fleet/constant/customEvents";
import { LOAD_EVENTS_V3_NAME } from "cortex/ui/src/fleet/redux-store/ui-state/events-v3/actions";
import { Application } from "cortex/ui/src/fleet/view/application/model";
import { useAppDispatch } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import type { StringOption } from "cortex/ui/src/ui/table/types";
import { User } from "cortex/ui/src/user/model";

import AlertForm from "./AlertForm";
import { alertToForm, createOrEditAlert, validateAlert } from "../action";
import { Alert, FormErrors, FormValues, defaultFormValues } from "../model";

type EventNamesResponse =
  | {
      data?: {
        data?: string[];
      };
    }
  | undefined;

type AlertPreset = (typeof alertPresetsList)[number];

function presetToForm(
  current: FormValues,
  preset: AlertPreset,
): Partial<FormValues> {
  return {
    eventNames: [...preset.eventNames],
    frequency: preset.frequency,
    name: preset.label,
    priorities: [...preset.priorities],
    sources: [...preset.sources],
    threshold: preset.threshold ?? current.threshold,
  };
}

interface AlertEditDrawerProps {
  alert: Alert;
  applications?: ReadonlyArray<Application>;
  onCancel: () => void;
  open: boolean;
  targetOpts?: StringOption[];
  users?: ReadonlyArray<User>;
}

export default function AlertEditDrawer({
  alert,
  applications = [],
  onCancel,
  open,
  targetOpts = [],
  users = [],
}: AlertEditDrawerProps) {
  const dispatch = useAppDispatch();

  const [form, setForm] = useState<FormValues>(() => alertToForm(alert));
  const [preset, setPreset] = useState<StringOption | null>({
    label: customAlertPreset.label,
    value: customAlertPreset.label,
  });
  const presetOptions = useMemo(
    () =>
      (
        groupedPresetOptions as Array<{
          label: string;
          options: StringOption[];
        }>
      ).flatMap(({ label: group, options }) =>
        options.map((opt) => {
          const label = opt.label ?? opt.value ?? "";
          const value = opt.value ?? label;
          return { ...opt, label, value, group };
        }),
      ),
    [],
  );

  const [errors, setErrors] = useState<FormErrors>({});
  const [eventNameOptions, setEventNameOptions] = useState<StringOption[]>([]);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const sourceOptions = useMemo(() => {
    const appOptions = applications.map((a) => {
      return {
        isDisabled: !a.available,
        label: a.name,
        value: a.id,
      };
    });

    return [...sourceOptionsMap, ...appOptions];
  }, [applications]);

  const getEventNames = useCallback(() => {
    return createPromiseApiAction(dispatch, LOAD_EVENTS_V3_NAME);
  }, [dispatch]);

  useEffect(() => {
    getEventNames().then((events) => {
      const typedEvents = events as EventNamesResponse;
      const eventOptions = (typedEvents?.data?.data ?? []).map((event) => ({
        label: event,
        value: event,
      }));
      setEventNameOptions(eventOptions);
    });
  }, [getEventNames]);

  const handleCancel = useCallback(() => {
    if (submitted) {
      setSubmitted(false);
    }
    setForm(defaultFormValues);
    onCancel();
  }, [onCancel, submitted]);

  const handleEditAlert = useCallback(async (): Promise<Boolean | null> => {
    const validation = validateAlert(form);
    setErrors(validation);
    if (Object.keys(validation).length > 0) return null;
    setSubmitted(true);
    try {
      const result = await dispatch(createOrEditAlert(form));
      showSuccess("Your alert was edited.");
      handleCancel();
      return result;
    } catch (_err) {
      setSubmitted(false);
      return null;
    }
  }, [dispatch, form, handleCancel]);

  function onPresetChange(opt: StringOption | null) {
    setPreset(opt);
    if (!opt) return;
    const preset = alertPresetsList.find((p) => p.label === opt.label);
    if (preset) {
      setForm((current) => ({ ...current, ...presetToForm(current, preset) }));
    } else if (opt.label === customAlertPreset.label) {
      setForm(alertToForm(alert));
    }
  }

  return (
    <Drawer
      cancelLabel="Cancel"
      commitLabel="Save"
      commitLoading={submitted}
      onCancel={handleCancel}
      onClose={handleCancel}
      onCommit={() => {
        void handleEditAlert();
      }}
      open={open}
      size="large"
      title="Edit Alert"
    >
      <AlertForm
        errors={errors}
        eventNameOptions={eventNameOptions}
        onChange={setForm}
        onPresetChange={onPresetChange}
        presetOptions={presetOptions}
        presetState={preset}
        setErrors={setErrors}
        sourceOptions={sourceOptions}
        targetOpts={targetOpts}
        users={users}
        value={form}
      />
    </Drawer>
  );
}
